// Fix scroll position on load
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
window.scrollTo(0, 0);

// === THEME LOADING (UI Data Preparation) ===
function loadThemesFromConfig() {
    if (!window.THEME_CONFIG_V2) {
        console.error('[theme] window.THEME_CONFIG_V2 not found');
        return { themes: [], themeStyles: {}, themeSections: [] };
    }

    const themes = [];
    const themeStyles = {};
    const categoryMap = {
        'defaults': [],
        'omarchy': [],
        'custom': []
    };

    Object.entries(window.THEME_CONFIG_V2).forEach(([themeId, config]) => {
        const displayName = config.displayName || themeId.replace('theme-', '').replace(/-/g, ' ');

        // Determine category
        let category = config.category;
        if (!category) {
            if (themeId.startsWith('theme-omarchy-')) {
                category = 'omarchy';
            } else {
                // Default fallback if no category specified
                category = 'defaults';
            }
        }

        // Map 'custom' to 'defaults' or handle it if we want a 'custom' section
        if (category === 'custom') category = 'defaults';

        const cleanName = themeId.startsWith('theme-omarchy-')
            ? themeId.replace('theme-omarchy-', '').replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : displayName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

        const themeObj = {
            id: themeId,
            name: cleanName,
            group: category,
            bgColor: config.bg,
            textColor: config.text,
            __position: themes.length
        };

        themes.push(themeObj);

        if (!categoryMap[category]) categoryMap[category] = [];
        categoryMap[category].push(themeObj);

        // Keep styles for UI preview/logic if needed
        themeStyles[themeId] = {
            bg: config.bg,
            bgSecondary: config.bgAlt,
            text: config.text,
            // ... minimal data needed for UI
        };
    });

    const DEFAULT_THEME_ORDER = ['theme-morning', 'theme-daylight', 'theme-night'];

    if (categoryMap.defaults) {
        categoryMap.defaults.sort((a, b) => {
            const idxA = DEFAULT_THEME_ORDER.indexOf(a.id);
            const idxB = DEFAULT_THEME_ORDER.indexOf(b.id);

            if (idxA !== -1 || idxB !== -1) {
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            }
            return (a.__position ?? 0) - (b.__position ?? 0);
        });
    }

    const themeSections = [
        {
            id: 'defaults',
            label: 'Instacalc Collection',
            items: categoryMap.defaults || []
        },
        {
            id: 'omarchy',
            label: 'Omarchy Collection',
            items: categoryMap.omarchy || []
        }
    ].filter(section => section.items.length > 0);

    const moreThemes = categoryMap.custom || [];

    const stripPosition = (items = []) => items.map(item => {
        const { __position, ...rest } = item;
        return rest;
    });

    return {
        themes: themes.map(({ __position, ...rest }) => rest),
        themeStyles,
        themeSections: themeSections.map(section => ({
            ...section,
            items: stripPosition(section.items)
        })),
        moreThemes: stripPosition(moreThemes)
    };
}

const { themes, themeStyles, themeSections, moreThemes } = loadThemesFromConfig();

// Expose to window for Alpine.js theme panel
window.themeSections = themeSections;
window.themeStyles = themeStyles;

// Alpine.js data function for theme panel
function themePanel() {
    // Get defaults from shared config (single source of truth)
    const defaults = window.THEME_DEFAULTS;
    if (!defaults) {
        console.error('[themePanel] THEME_DEFAULTS not found - theme.js must load before switcher.js');
    }

    return {
        // Mobile detection - check if we're in the mobile dock
        isMobileDock() {
            return this.$store?.ui?.mobiletab === 'toolbar';
        },

        // Get data from global theme state via ThemeSystem
        themeSections: window.themeSections || [],
        themeStyles: window.themeStyles || {},

        // =============================================
        // LOCAL REACTIVE PROPERTIES: Alpine tracks assignments to these
        // syncState() copies from ThemeSystem.state to these properties
        // This is required for Alpine reactivity - getters to external state don't work
        // =============================================
        currentThemeId: window.ThemeSystem.state.themeId,
        currentBackgroundStyle: window.ThemeSystem.state.bgStyle,
        currentHeaderCardStyle: window.ThemeSystem.state.cardStyles.header,
        currentSidebarCardStyle: window.ThemeSystem.state.cardStyles.sidebar,
        currentCalcCardStyle: window.ThemeSystem.state.cardStyles.calc,
        currentFontStyle: window.ThemeSystem.state.fontStyle,
        customFontName: window.ThemeSystem.state.customFontName,
        fontLocked: window.ThemeSystem.state.fontLocked,
        currentSizeStyle: window.ThemeSystem.state.sizeStyle,
        currentShowBottomNav: window.ThemeSystem.state.showBottomNav,
        currentGradientIntensity: window.ThemeSystem.getGradientIntensity(),
        gradientIntensity: window.ThemeSystem.getGradientIntensity(),
        hasCustomGradientIntensity: window.ThemeSystem.state.gradientIntensity !== null,
        currentGridSize: window.ThemeSystem.getGridSize?.() || 50,
        gridSize: window.ThemeSystem.getGridSize?.() || 50,
        currentWaveStyle: window.ThemeSystem.getWaveStyle?.() || 2,
        waveStyle: window.ThemeSystem.getWaveStyle?.() || 2,
        currentDockStyle: window.ThemeSystem.getDockStyle?.() || 'default',
        dockStyle: window.ThemeSystem.getDockStyle?.() || 'default',
        dockStyles: ['default', 'minimal'],
        currentDockPosition: window.ThemeSystem.getDockPosition?.() || 'right',
        dockPosition: window.ThemeSystem.getDockPosition?.() || 'right',
        dockPositions: ['left', 'center', 'right'],

        // Presets state
        presets: [],
        newPresetName: '',
        showPresetInput: false,
        activePresetId: null, // Currently active preset
        activePresetString: null, // Share string when preset was applied (to detect changes)

        // Custom Color Builder
        showCustomColorBuilder: false,
        customColors: {
            // Base UI colors
            bg: '#ffffff',
            bgAlt: '#f8f9fa',
            text: '#333333',
            textDim: '#888888',
            accent: '#3b82f6',
            border: '#e5e7eb',
            // Calc-specific colors
            result: '#7c3aed',
            comment: '#10b981',
            rowType: '#3b82f6',
            rowTypeCustom: '#ec4899'
        },
        savedCustomColors: null, // Stores the last saved custom color set

        // Check if we have saved custom colors
        hasSavedCustomColors() {
            return window.ThemeSystem?.state?.customColors !== null &&
                window.ThemeSystem?.state?.customColors !== undefined;
        },

        // Load saved custom colors from ThemeSystem.state
        loadSavedCustomColors() {
            const saved = window.ThemeSystem?.state?.customColors;
            if (saved) {
                this.savedCustomColors = { ...saved };
                return this.savedCustomColors;
            }
            return null;
        },

        // Save custom colors to ThemeSystem.state (which auto-saves to localStorage)
        saveCustomColors() {
            if (window.ThemeSystem) {
                window.ThemeSystem.state.customColors = { ...this.customColors };
                window.ThemeSystem.saveState();
                this.savedCustomColors = { ...this.customColors };
            }
        },

        // Check if current theme is using custom colors
        isCustomTheme() {
            return this.currentThemeId === 'theme-custom';
        },

        // Calculate contrast ratio between two colors (for accessibility checking)
        getContrastRatio(color1, color2) {
            const getLuminance = (color) => {
                // Remove # if present
                color = color.replace('#', '');
                const rgb = parseInt(color, 16);
                const r = ((rgb >> 16) & 0xff) / 255;
                const g = ((rgb >> 8) & 0xff) / 255;
                const b = ((rgb >> 0) & 0xff) / 255;

                const [rs, gs, bs] = [r, g, b].map(val => {
                    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
                });

                return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
            };

            const lum1 = getLuminance(color1);
            const lum2 = getLuminance(color2);
            const brightest = Math.max(lum1, lum2);
            const darkest = Math.min(lum1, lum2);
            return (brightest + 0.05) / (darkest + 0.05);
        },

        resetCustomColorsToTheme() {
            // Reset custom colors to current theme's colors
            this.openCustomColorBuilder();
        },

        openCustomColorBuilder() {
            // Helper to get hex from computed style
            const getHex = (varName) => {
                const style = getComputedStyle(document.documentElement);
                let val = style.getPropertyValue(varName).trim();
                if (!val) return '#000000';

                // Handle hex
                if (val.startsWith('#')) {
                    return val.length === 4 ? '#' + val[1] + val[1] + val[2] + val[2] + val[3] + val[3] : val;
                }

                // Handle rgb/rgba
                if (val.startsWith('rgb')) {
                    const match = val.match(/[\d.]+/g);
                    if (match && match.length >= 3) {
                        const r = parseInt(match[0]);
                        const g = parseInt(match[1]);
                        const b = parseInt(match[2]);
                        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
                    }
                }
                return '#000000'; // Fallback
            };

            this.customColors = {
                bg: getHex('--bg'),
                bgAlt: getHex('--bg-alt'),
                text: getHex('--text'),
                textDim: getHex('--text-dim'),
                accent: getHex('--accent'),
                border: getHex('--border-color'),
                result: getHex('--calc-result-color'),
                comment: getHex('--calc-comment-color'),
                rowType: getHex('--calc-row-type-color'),
                rowTypeCustom: getHex('--calc-row-type-custom-color')
            };
            this.showCustomColorBuilder = true;
        },

        closeCustomColorBuilder() {
            this.showCustomColorBuilder = false;
        },

        loadThemeColors(themeId) {
            if (!themeId) return;

            // Get the theme config
            const themeConfig = window.THEME_CONFIG_V2?.[themeId];
            if (!themeConfig) {
                console.warn('[loadThemeColors] Theme not found:', themeId);
                return;
            }

            // Get the theme's background color for alpha blending
            const themeBg = themeConfig.surfaces?.bg || themeConfig.bg || '#ffffff';

            // Helper to extract hex color from any format, blending alpha if needed
            const getHex = (color, blendBg = themeBg) => {
                if (!color) return '#000000';

                // If already hex, return it
                if (color.startsWith('#')) return color;

                // Check if it's an rgba with alpha we need to blend
                const rgbaMatch = color.match(/^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
                if (rgbaMatch) {
                    const r = parseFloat(rgbaMatch[1]);
                    const g = parseFloat(rgbaMatch[2]);
                    const b = parseFloat(rgbaMatch[3]);
                    const a = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;

                    if (a < 1 && blendBg) {
                        // Blend against background - parse bg hex
                        let bgR = 255, bgG = 255, bgB = 255;
                        if (blendBg.startsWith('#')) {
                            const bgHex = blendBg.replace('#', '');
                            bgR = parseInt(bgHex.substring(0, 2), 16);
                            bgG = parseInt(bgHex.substring(2, 4), 16);
                            bgB = parseInt(bgHex.substring(4, 6), 16);
                        }
                        // Alpha blend: result = fg * alpha + bg * (1 - alpha)
                        const blendR = Math.round(r * a + bgR * (1 - a));
                        const blendG = Math.round(g * a + bgG * (1 - a));
                        const blendB = Math.round(b * a + bgB * (1 - a));
                        return `#${blendR.toString(16).padStart(2, '0')}${blendG.toString(16).padStart(2, '0')}${blendB.toString(16).padStart(2, '0')}`;
                    }

                    // No alpha or blendBg, just convert rgb to hex
                    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
                }

                // For other formats (like color names), use temporary element
                const temp = document.createElement('div');
                temp.style.color = color;
                document.body.appendChild(temp);
                const computed = getComputedStyle(temp).color;
                document.body.removeChild(temp);

                // Recursively parse the computed value
                return getHex(computed, blendBg);
            };

            // Load colors from theme config
            this.customColors = {
                bg: getHex(themeConfig.surfaces?.bg || themeConfig.bg),
                bgAlt: getHex(themeConfig.surfaces?.bgAlt || themeConfig.bgAlt),
                text: getHex(themeConfig.surfaces?.text || themeConfig.text),
                textDim: getHex(themeConfig.surfaces?.textDim || themeConfig.textDim),
                accent: getHex(themeConfig.accents?.accent || themeConfig.accent),
                border: getHex(themeConfig.borderColor),
                result: getHex(themeConfig.calcResultColor),
                comment: getHex(themeConfig.calcCommentColor),
                rowType: getHex(themeConfig.calcRowTypeColor),
                rowTypeCustom: getHex(themeConfig.calcRowTypeCustomColor)
            };

            // Preview the colors immediately
            this.previewCustomColors();
        },

        previewCustomColors() {
            // Live preview: apply colors immediately as user changes them
            const root = document.documentElement;

            // Validate hex colors before applying
            const isValidHex = (color) => /^#?[0-9A-Fa-f]{6}$/.test(color);

            // Apply each color if valid
            if (isValidHex(this.customColors.bg)) {
                root.style.setProperty('--bg', this.customColors.bg);
                root.style.setProperty('--main-bg', this.customColors.bg);
            }
            if (isValidHex(this.customColors.bgAlt)) {
                root.style.setProperty('--bg-alt', this.customColors.bgAlt);
            }
            if (isValidHex(this.customColors.text)) {
                root.style.setProperty('--text', this.customColors.text);
            }
            if (isValidHex(this.customColors.textDim)) {
                root.style.setProperty('--text-dim', this.customColors.textDim);
            }
            if (isValidHex(this.customColors.accent)) {
                root.style.setProperty('--accent', this.customColors.accent);
            }
            if (isValidHex(this.customColors.border)) {
                root.style.setProperty('--border-color', this.customColors.border);
            }
            if (isValidHex(this.customColors.result)) {
                root.style.setProperty('--calc-result-color', this.customColors.result);
            }
            if (isValidHex(this.customColors.comment)) {
                root.style.setProperty('--calc-comment-color', this.customColors.comment);
            }
            if (isValidHex(this.customColors.rowType)) {
                root.style.setProperty('--calc-row-type-color', this.customColors.rowType);
            }
            if (isValidHex(this.customColors.rowTypeCustom)) {
                root.style.setProperty('--calc-row-type-custom-color', this.customColors.rowTypeCustom);
            }
        },

        applyCustomColors() {
            const strip = (c) => c ? c.replace('#', '') : '';

            // Save colors to localStorage for later use
            this.saveCustomColors();

            // Build compact colors string: colors=bg:fff,text:333,...
            const colorPairs = [];
            if (this.customColors.bg) colorPairs.push(`bg:${strip(this.customColors.bg)}`);
            if (this.customColors.bgAlt) colorPairs.push(`bgalt:${strip(this.customColors.bgAlt)}`);
            if (this.customColors.text) colorPairs.push(`text:${strip(this.customColors.text)}`);
            if (this.customColors.textDim) colorPairs.push(`dim:${strip(this.customColors.textDim)}`);
            if (this.customColors.accent) colorPairs.push(`accent:${strip(this.customColors.accent)}`);
            if (this.customColors.border) colorPairs.push(`border:${strip(this.customColors.border)}`);
            if (this.customColors.result) colorPairs.push(`result:${strip(this.customColors.result)}`);
            if (this.customColors.comment) colorPairs.push(`comment:${strip(this.customColors.comment)}`);
            if (this.customColors.rowType) colorPairs.push(`type:${strip(this.customColors.rowType)}`);
            if (this.customColors.rowTypeCustom) colorPairs.push(`typecustom:${strip(this.customColors.rowTypeCustom)}`);

            // Build full share string: custom|colors=...
            const fullString = `custom|colors=${colorPairs.join(',')}`;

            // Save custom colors to ThemeSystem state
            window.ThemeSystem.state.customColors = { ...this.customColors };
            window.ThemeSystem.state.themeId = 'theme-custom';

            // Apply the colors via ThemeSystem
            window.ThemeSystem.applyCustomColors(this.customColors);
            window.ThemeSystem.saveState();
            window.ThemeSystem.notifyChange();
            this.syncState();

            this.closeCustomColorBuilder();

            // Copy to clipboard and show feedback
            navigator.clipboard.writeText(fullString).then(() => {
                this.copyStringFeedback = 'Copied!';
                setTimeout(() => { this.copyStringFeedback = ''; }, 2000);
            }).catch(err => {
                console.error('Failed to copy to clipboard:', err);
            });
        },

        // Apply saved custom colors (or defaults if none saved)
        applySavedCustomColors() {
            console.log('[ThemeSwitcher] applySavedCustomColors called');
            const saved = this.loadSavedCustomColors();
            if (saved) {
                this.customColors = { ...saved };
            }
            // Apply the colors (either saved or current defaults)
            this.previewCustomColors();
            // Save customColors to ThemeSystem state first
            window.ThemeSystem.state.customColors = { ...this.customColors };
            // Update ThemeSystem state so it persists and syncState() reads it back correctly
            window.ThemeSystem.state.themeId = 'theme-custom';
            console.log('[ThemeSwitcher] Set themeId to:', window.ThemeSystem.state.themeId);
            window.ThemeSystem.saveState();
            window.ThemeSystem.notifyChange();
            this.syncState();
            console.log('[ThemeSwitcher] After syncState, currentThemeId:', this.currentThemeId);
        },

        // Delete custom colors
        deleteCustomColors() {
            if (confirm('Delete your custom theme? This cannot be undone.')) {
                try {
                    localStorage.removeItem('custom-theme-colors');
                    this.savedCustomColors = null;
                    this.closeCustomColorBuilder();

                    // If currently using custom colors, switch to a default theme
                    if (this.isCustomTheme()) {
                        this.selectTheme('theme-omarchy-tokyo');
                    }
                } catch (e) {
                    console.error('Failed to delete custom colors:', e);
                }
            }
        },

        // Share & Presets state (combined section)
        showSharePresets: true, // Expanded by default
        importString: '',
        showImportInput: true, // Expanded by default
        copyStringFeedback: '', // Feedback for copy string button
        copyUrlFeedback: '', // Feedback for copy URL button
        openMenuPresetId: null, // Track which preset menu is open
        renamePresetId: null, // Track which preset is being renamed
        renamePresetName: '', // New name for preset being renamed

        // Live-updating share string (reactive - re-computed when state changes)
        // We reference reactive properties to trigger Alpine reactivity
        get shareString() {
            // Touch reactive properties to make Alpine re-evaluate this getter
            // when any of these change
            void this.currentThemeId;
            void this.currentBackgroundStyle;
            void this.currentFontStyle;
            void this.currentSizeStyle;
            void this.currentCalcCardStyle;
            void this.currentGradientIntensity;
            void this.currentGridSize;
            void this.currentWaveStyle;
            return window.ThemeSystem.exportConfig();
        },

        // Check if active preset has been modified
        get isPresetModified() {
            if (!this.activePresetId || !this.activePresetString) return false;
            return this.shareString !== this.activePresetString;
        },

        // Current theme config (computed)
        get themeConfig() {
            return window.THEME_CONFIG_V2?.[this.currentThemeId] || {};
        },

        // Options - from shared defaults (single source of truth)
        get backgroundStyles() { return defaults?.options?.backgroundStyles || ['gradient', 'solid', 'grid']; },
        get cardStyleOptions() { return defaults?.options?.cardStyleOptions || ['elevated', 'sunken', 'flat', 'glass']; },
        get calcCardStyleOptions() { return ['elevated', 'flat', 'glass']; },
        get fontStyles() { return defaults?.options?.fontStyles || ['default', 'clean', 'mono', 'serif', 'hand', 'custom']; },
        get sizeStyles() { return defaults?.options?.sizeStyles || ['spacious', 'compact']; },
        get bottomNavOptions() { return defaults?.options?.bottomNavOptions || ['header', 'floating']; },

        get isOmarchyTheme() {
            return this.currentThemeId && this.currentThemeId.startsWith('theme-omarchy-');
        },

        // Palette preset (local reactive property, synced from ThemeSystem)
        currentPalettePresetLocal: null,

        get savedPalettePreset() {
            // Use shared default helper
            const defaultPreset = defaults?.getDefaultPalettePreset?.(this.currentThemeId)
                || (this.isOmarchyTheme ? 'vscode-style' : 'default');
            return window.ThemeSystem.state.palettePresets?.[this.currentThemeId] || defaultPreset;
        },

        // Alias for template compatibility - use local property if set, otherwise from ThemeSystem
        get currentPalettePreset() {
            return this.currentPalettePresetLocal || this.savedPalettePreset;
        },

        // Default card style getters - from shared defaults (single source of truth)
        getDefaultHeaderCardStyle() {
            return defaults?.getDefaultCardStyle?.('header') || defaults?.cardStyles?.header || 'flat';
        },
        getDefaultCalcCardStyle() {
            return defaults?.getDefaultCardStyle?.('calc') || defaults?.cardStyles?.calc || 'elevated';
        },
        getDefaultSidebarCardStyle() {
            return defaults?.getDefaultCardStyle?.('sidebar') || defaults?.cardStyles?.sidebar || 'flat';
        },

        get displayFontStyle() {
            if (this.customFontName) {
                return `Custom: ${this.customFontName}`;
            }
            return 'Custom';
        },

        get palettePresets() {
            if (this.isOmarchyTheme) {
                const presets = window.DesignLabPalette?.listPresets?.() || [];
                return presets.map(preset => ({
                    ...preset,
                    label: this.getShortLabel(preset.key)
                }));
            } else {
                return [
                    { key: 'default', label: 'Default', description: 'Theme default colors' },
                    { key: 'rotation-1', label: 'Type 1', description: 'Color variation 1' },
                    { key: 'rotation-2', label: 'Type 2', description: 'Color variation 2' },
                    { key: 'rotation-3', label: 'Type 3', description: 'Color variation 3' }
                ];
            }
        },

        getShortLabel(key) {
            const labels = {
                'default': 'Default',
                'vscode-style': 'VS Code',
                'vim-style': 'Vim',
                'btop-style': 'btop',
                'terminal-style': 'Terminal',
                'pastel-notebook': 'Pastel',
                'contrast-pop': 'Contrast'
            };
            return labels[key] || key;
        },

        // Helper methods for UI styling (fixes ReferenceErrors)
        getOptionButtonStyle(isSelected) {
            if (isSelected) {
                return 'background: var(--accent); color: var(--bg);';
            }
            return 'background: var(--bg); color: var(--text); border: 1px solid var(--border-color); opacity: 0.7;';
        },

        getOptionButtonClass() {
            return 'px-2 py-1 text-xs rounded transition';
        },

        // Actions delegate to ThemeSystem, then syncState() for instant UI feedback
        selectTheme(themeId) {
            window.ThemeSystem.setTheme(themeId);
            this.syncState();
        },

        selectPalette(presetKey) {
            this.currentPalettePresetLocal = presetKey;
            window.ThemeSystem.applyPalettePreset(presetKey);
            this.syncState();
        },

        selectBackgroundStyle(style) {
            window.ThemeSystem.setBackgroundStyle(style);
            this.syncState();
        },

        selectHeaderCardStyle(style) {
            window.ThemeSystem.setCardStyle('header', style);
            this.syncState();
        },

        selectSidebarCardStyle(style) {
            window.ThemeSystem.setCardStyle('sidebar', style);
            this.syncState();
        },

        selectCalcCardStyle(style) {
            window.ThemeSystem.setCardStyle('calc', style);
            this.syncState();
        },

        selectFontStyle(font) {
            window.ThemeSystem.setFontStyle(font);
            this.syncState();
            if (font === 'custom') {
                if (typeof window.loadFontPickerFloater === 'function') {
                    window.loadFontPickerFloater();
                }
            }
        },

        toggleFontLock() {
            window.ThemeSystem.state.fontLocked = !window.ThemeSystem.state.fontLocked;
            window.ThemeSystem.saveState();
            window.ThemeSystem.notifyChange();
            this.syncState();
        },

        selectSizeStyle(size) {
            window.ThemeSystem.setSizeStyle(size);
            this.syncState();
        },

        selectBottomNavVisibility(option) {
            const show = option === 'floating';
            window.ThemeSystem.setBottomNav(show);
            this.syncState();
        },

        // Gradient intensity
        setGradientIntensity(value) {
            window.ThemeSystem.setGradientIntensity(value);
            this.syncState();
        },

        resetGradientIntensity() {
            window.ThemeSystem.resetGradientIntensity();
            this.syncState();
        },

        // Grid size
        setGridSize(value) {
            window.ThemeSystem.setGridSize(value);
            this.syncState();
        },

        // Wave style
        setWaveStyle(value) {
            window.ThemeSystem.setWaveStyle(value);
            this.syncState();
        },

        // Dock style
        setDockStyle(value) {
            window.ThemeSystem.setDockStyle(value);
            this.syncState();
        },

        // Dock position
        setDockPosition(value) {
            window.ThemeSystem.setDockPosition(value);
            this.syncState();
        },

        // Sync local reactive state with global ThemeSystem state
        syncState() {
            const state = window.ThemeSystem.state;
            this.currentThemeId = state.themeId;
            this.currentBackgroundStyle = state.bgStyle;
            this.currentHeaderCardStyle = state.cardStyles.header;
            this.currentSidebarCardStyle = state.cardStyles.sidebar;
            this.currentCalcCardStyle = state.cardStyles.calc;
            this.currentFontStyle = state.fontStyle;
            this.customFontName = state.customFontName;
            this.fontLocked = state.fontLocked;
            this.currentSizeStyle = state.sizeStyle;
            this.currentShowBottomNav = state.showBottomNav;
            this.currentGradientIntensity = window.ThemeSystem.getGradientIntensity();
            this.gradientIntensity = window.ThemeSystem.getGradientIntensity();
            this.hasCustomGradientIntensity = state.gradientIntensity !== null;
            this.currentGridSize = window.ThemeSystem.getGridSize() || 50;
            this.gridSize = window.ThemeSystem.getGridSize() || 50;
            this.currentWaveStyle = window.ThemeSystem.getWaveStyle() || 2;
            this.waveStyle = window.ThemeSystem.getWaveStyle() || 2;
            this.currentDockStyle = window.ThemeSystem.getDockStyle() || 'default';
            this.dockStyle = window.ThemeSystem.getDockStyle() || 'default';
            this.currentDockPosition = window.ThemeSystem.getDockPosition() || 'right';
            this.dockPosition = window.ThemeSystem.getDockPosition() || 'right';
            this.currentPalettePresetLocal = state.palettePresets?.[state.themeId] || null;
        },

        // ============================================
        // PRESET MANAGEMENT
        // ============================================
        // Helper to convert config object to display string
        configToString(config) {
            if (typeof config === 'string') return config;

            const parts = [];
            // Theme ID (remove prefixes)
            const shortId = (config.themeId || '')
                .replace(/^theme-omarchy-/, '')
                .replace(/^theme-/, '');
            if (shortId) parts.push(shortId);

            // Background
            if (config.bgStyle) parts.push(`bg=${config.bgStyle}`);

            // Font
            if (config.fontStyle && config.fontStyle !== 'default') {
                parts.push(`font=${config.fontStyle}`);
            }

            // Size
            if (config.sizeStyle && config.sizeStyle !== 'spacious') {
                parts.push(`size=${config.sizeStyle}`);
            }

            return parts.join('|') || 'custom';
        },

        // Computed getter that always includes "Current" preset first
        get allPresets() {
            const currentPreset = {
                id: 'current',
                name: 'Current',
                config: this.shareString,
                isLive: true
            };
            // Convert saved preset configs (objects) to strings for display
            const presetsWithStringConfig = this.presets.map(preset => {
                return {
                    ...preset,
                    config: this.configToString(preset.config),
                    configObj: preset.config // Keep original for applying
                };
            });
            return [currentPreset, ...presetsWithStringConfig];
        },

        initPresets() {
            this.presets = window.ThemeSystem.loadPresets();
        },

        saveAsPreset() {
            if (!this.newPresetName.trim()) return;
            window.ThemeSystem.createPreset(this.newPresetName);
            this.presets = window.ThemeSystem.loadPresets();
            this.newPresetName = '';
            this.showPresetInput = false;
        },

        applyPreset(presetId) {
            // "Current" is special - it's already the live state, so don't apply
            if (presetId === 'current') {
                this.activePresetId = null;
                this.activePresetString = null;
                return;
            }

            window.ThemeSystem.applyPreset(presetId);
            // Note: No manual syncState() needed - applyPreset() calls notifyChange()
            // Track this as the active preset
            this.activePresetId = presetId;
            this.activePresetString = this.shareString;
        },

        deletePreset(presetId) {
            // Can't delete "Current" - it's a live preset
            if (presetId === 'current') return;

            window.ThemeSystem.deletePreset(presetId);
            this.presets = window.ThemeSystem.loadPresets();
            // Clear active if we deleted it
            if (this.activePresetId === presetId) {
                this.activePresetId = null;
                this.activePresetString = null;
            }
        },

        // Update the active preset with current settings
        updateActivePreset() {
            if (!this.activePresetId) return;
            const preset = this.presets.find(p => p.id === this.activePresetId);
            if (!preset) return;

            // Delete old and create new with same name
            window.ThemeSystem.deletePreset(this.activePresetId);
            const newPreset = window.ThemeSystem.createPreset(preset.name);
            this.presets = window.ThemeSystem.loadPresets();

            // Update tracking
            this.activePresetId = newPreset.id;
            this.activePresetString = this.shareString;
        },

        // Menu actions
        toggleMenu(presetId) {
            this.openMenuPresetId = this.openMenuPresetId === presetId ? null : presetId;
        },

        startRename(presetId) {
            const preset = this.presets.find(p => p.id === presetId);
            if (!preset) return;
            this.renamePresetId = presetId;
            this.renamePresetName = preset.name;
            this.openMenuPresetId = null;
        },

        saveRename() {
            if (!this.renamePresetId || !this.renamePresetName.trim()) return;
            const preset = this.presets.find(p => p.id === this.renamePresetId);
            if (!preset) return;

            // Delete old and create new with new name
            window.ThemeSystem.deletePreset(this.renamePresetId);
            const newPreset = window.ThemeSystem.createPreset(this.renamePresetName, preset.config);
            this.presets = window.ThemeSystem.loadPresets();

            // Update tracking if this was the active preset
            if (this.activePresetId === this.renamePresetId) {
                this.activePresetId = newPreset.id;
            }

            this.renamePresetId = null;
            this.renamePresetName = '';
        },

        cancelRename() {
            this.renamePresetId = null;
            this.renamePresetName = '';
        },

        copyPresetConfig(presetId) {
            const preset = this.allPresets.find(p => p.id === presetId);
            if (!preset) return;

            // For "Current" preset, use the config string directly
            // For saved presets, apply and export to get full string
            let configString = preset.config;
            if (preset.configObj) {
                // Temporarily apply the preset config and export it
                const oldState = { ...window.ThemeSystem.state };
                Object.assign(window.ThemeSystem.state, preset.configObj);
                configString = window.ThemeSystem.exportConfig();
                Object.assign(window.ThemeSystem.state, oldState);
            }

            navigator.clipboard.writeText(configString);
            this.openMenuPresetId = null;
        },

        copyPresetUrl(presetId) {
            const preset = this.allPresets.find(p => p.id === presetId);
            if (!preset) return;

            // For "Current" preset, use the config string directly
            // For saved presets, apply and export to get full string
            let configString = preset.config;
            if (preset.configObj) {
                // Temporarily apply the preset config and export it
                const oldState = { ...window.ThemeSystem.state };
                Object.assign(window.ThemeSystem.state, preset.configObj);
                configString = window.ThemeSystem.exportConfig();
                Object.assign(window.ThemeSystem.state, oldState);
            }

            const url = `${window.location.origin}/?theme=${configString}`;
            navigator.clipboard.writeText(url);
            this.openMenuPresetId = null;
        },

        // ============================================
        // SHARE & IMPORT
        // ============================================
        copyShareString() {
            navigator.clipboard.writeText(this.shareString).then(() => {
                // Could add a toast notification here
            });
        },

        applyImportString() {
            if (!this.importString.trim()) return;

            let themeString = this.importString.trim();

            // If it looks like a URL, extract the theme parameter
            if (themeString.includes('://') || themeString.startsWith('http') || themeString.includes('?theme=')) {
                try {
                    // Try to parse as URL
                    const url = new URL(themeString.startsWith('http') ? themeString : `https://${themeString}`);
                    const themeParam = url.searchParams.get('theme');
                    if (themeParam) {
                        themeString = themeParam;
                    }
                } catch (e) {
                    // If URL parsing fails, try to extract manually
                    const match = themeString.match(/[?&]theme=([^&]+)/);
                    if (match) {
                        themeString = decodeURIComponent(match[1]);
                    }
                }
            }

            const success = window.ThemeSystem.importConfig(themeString);
            if (success) {
                // Note: No manual syncState() needed - importConfig() calls notifyChange()
                this.importString = '';
                this.showImportInput = false;
            } else {
                console.warn('[themePanel] Failed to import theme config');
            }
        },

        copyThemeString() {
            const configString = window.ThemeSystem.exportConfig();
            navigator.clipboard.writeText(configString).then(() => {
                // Show temporary feedback
                this.copyStringFeedback = 'Copied!';
                setTimeout(() => { this.copyStringFeedback = ''; }, 2000);
            }).catch(err => {
                console.error('[themePanel] Failed to copy string:', err);
                this.copyStringFeedback = 'Failed';
                setTimeout(() => { this.copyStringFeedback = ''; }, 2000);
            });
        },

        copyThemeUrl() {
            const configString = window.ThemeSystem.exportConfig();
            const url = `${window.location.origin}/?theme=${configString}`;
            navigator.clipboard.writeText(url).then(() => {
                // Show temporary feedback
                this.copyUrlFeedback = 'Copied!';
                setTimeout(() => { this.copyUrlFeedback = ''; }, 2000);
            }).catch(err => {
                console.error('[themePanel] Failed to copy URL:', err);
                this.copyUrlFeedback = 'Failed';
                setTimeout(() => { this.copyUrlFeedback = ''; }, 2000);
            });
        },

        toggleSharePresets() {
            this.showSharePresets = !this.showSharePresets;
        },

        // Check if gradient and solid look the same
        get gradientLooksSolid() {
            return window.ThemeSystem.gradientLooksSolid();
        },

        // Runtime validation - called by Alpine's x-init
        init() {
            // Load saved custom colors if available
            this.loadSavedCustomColors();

            // Validate required dependencies
            const errors = [];

            if (!window.THEME_DEFAULTS) {
                errors.push('THEME_DEFAULTS not found');
            }
            if (!window.ThemeSystem) {
                errors.push('ThemeSystem not found');
            }
            if (!window.THEME_CONFIG_V2) {
                errors.push('THEME_CONFIG_V2 not found');
            }

            // Listen for global theme changes to keep UI in sync
            window.addEventListener('theme-state-changed', () => {
                this.syncState();
            });

            // Initialize presets
            this.initPresets();

            // Initial sync
            this.syncState();

            // Validate required properties exist on this component
            const requiredGetters = [
                'currentThemeId', 'currentPalettePreset', 'themeConfig',
                'currentBackgroundStyle', 'currentHeaderCardStyle',
                'currentCalcCardStyle', 'currentSidebarCardStyle',
                'backgroundStyles', 'cardStyleOptions', 'fontStyles', 'sizeStyles'
            ];
            const requiredMethods = [
                'getDefaultHeaderCardStyle', 'getDefaultCalcCardStyle', 'getDefaultSidebarCardStyle',
                'selectTheme', 'selectBackgroundStyle', 'selectFontStyle'
            ];

            requiredGetters.forEach(prop => {
                if (this[prop] === undefined) {
                    errors.push(`Missing getter: ${prop}`);
                }
            });

            requiredMethods.forEach(method => {
                if (typeof this[method] !== 'function') {
                    errors.push(`Missing method: ${method}`);
                }
            });

            if (errors.length > 0) {
                console.error('[themePanel] Validation failed:', errors);
            }
        }
    };
}

// Expose themePanel
window.themePanel = themePanel;

// Load Font Picker (UI function)
function loadFontPickerFloater() {
    if (window.InstacalcFontPicker) {
        const picker = document.getElementById('instacalc-font-picker');
        if (picker) {
            picker.style.display = 'block';
            picker.classList.remove('collapsed');
            picker.classList.add('expanded');
        }
        return;
    }
    const script = document.createElement('script');
    script.src = '/ui/dev/tools/font-picker-floater.js?v=' + Date.now();
    script.onload = () => {
        setTimeout(() => {
            const picker = document.getElementById('instacalc-font-picker');
            if (picker) {
                picker.classList.remove('collapsed');
                picker.classList.add('expanded');
            }
        }, 100);
    };
    document.head.appendChild(script);
}
window.loadFontPickerFloater = loadFontPickerFloater;
